import React, { useMemo } from "react";

const HEAT_LEVELS = { high: 3, max: 4 } as const;
const ISO_DATE_SLICE_END = 10;
const WEEKDAY_OFFSET = 6;
const DAYS_IN_WEEK = 7;
const LAST_MONTH_INDEX = 11;
const LAST_DAY_OF_MONTH = 31;
const FIRST_QUARTILE = 0.25;
const SECOND_QUARTILE = 0.5;
const THIRD_QUARTILE = 0.75;
const REFERENCE_WEEKDAY_YEAR = 2024;
const WEDNESDAY_INDEX = HEAT_LEVELS.high;
const THURSDAY_INDEX = HEAT_LEVELS.max;
const WEEKDAY_INDICES = { friday: 5, saturday: WEEKDAY_OFFSET } as const;
const FRIDAY_INDEX = WEEKDAY_INDICES.friday;
const SATURDAY_INDEX = WEEKDAY_OFFSET;
const COMPACT_LABEL_COLUMN_WIDTH = 14;
const FULL_LABEL_COLUMN_WIDTH = 26;
const TOP_PADDING = 22;
const COMPACT_FONT_SIZE = 9;
const DEFAULT_FONT_SIZE = 10;
const WEEKDAY_LABEL_OFFSET = 6;
const COMPACT_WEEKDAY_LABEL_INDICES = [0, 2, THURSDAY_INDEX, SATURDAY_INDEX] as const;
const FULL_WEEKDAY_LABEL_INDICES = [0, 1, 2, WEDNESDAY_INDEX, THURSDAY_INDEX, FRIDAY_INDEX, SATURDAY_INDEX] as const;

type PresenceHeatmapProps = {
    attendanceDates: string[];
    year?: number;
    colors?: [string, string, string, string, string];
    emptyColor?: string;
    cellSize?: number;
    gap?: number;
    showWeekdayLabels?: boolean;
    compact?: boolean;
    locale?: string;
};

type DayCell = {
    date: Date;
    iso: string;
    weekIndex: number;
    weekday: number;
    count: number;
    level: 0 | 1 | 2 | typeof HEAT_LEVELS.high | typeof HEAT_LEVELS.max;
};

function toISO(d: Date) {
    return d.toISOString().slice(0, ISO_DATE_SLICE_END);
}

const monIndex = (utcDay: number) => (utcDay + WEEKDAY_OFFSET) % DAYS_IN_WEEK;

const getLabelColumnWidth = (showLabels: boolean, isCompact: boolean) => {
    if (!showLabels) {
        return WEEKDAY_LABEL_OFFSET;
    }

    return isCompact ? COMPACT_LABEL_COLUMN_WIDTH : FULL_LABEL_COLUMN_WIDTH;
};

function levelForCount(count: number, thresholds: number[]) {
    if (count <= 0) return 0;
    if (count <= thresholds[0]) return 1;
    if (count <= thresholds[1]) return 2;
    if (count <= thresholds[2]) return HEAT_LEVELS.high;
    return HEAT_LEVELS.max;
}

export default function PresenceHeatmap({
                                            attendanceDates,
                                            year = new Date().getFullYear(),
                                            colors = ["#b01add", "#b01add", "#b01add", "#b01add", "#b01add"],
                                            emptyColor = "#2d2d2d",
                                            cellSize = 12,
                                            gap = 2,
                                            showWeekdayLabels = true,
                                            compact = false,
                                            locale = "en",
                                        }: PresenceHeatmapProps) {
    const { cells, monthLabels, weeks } = useMemo(() => {
        const countMap = new Map<string, number>();
        for (const iso of attendanceDates) {
            countMap.set(iso, (countMap.get(iso) ?? 0) + 1);
        }

        const start = new Date(Date.UTC(year, 0, 1));
        const end = new Date(Date.UTC(year, LAST_MONTH_INDEX, LAST_DAY_OF_MONTH));

        const startMonday = new Date(start);
        startMonday.setUTCDate(start.getUTCDate() - monIndex(start.getUTCDay()));

        const endSunday = new Date(end);
        endSunday.setUTCDate(end.getUTCDate() + (SATURDAY_INDEX - monIndex(end.getUTCDay())));

        const cells: DayCell[] = [];
        const monthLabels: { weekIndex: number; label: string }[] = [];

        let d = new Date(startMonday);
        let weekIndex = 0;
        let lastMonth = -1;

        while (d <= endSunday) {
            const weekday = monIndex(d.getUTCDay());
            const iso = toISO(d);
            const count = countMap.get(iso) ?? 0;

            cells.push({ date: new Date(d), iso, weekIndex, weekday, count, level: 0 });

            d.setUTCDate(d.getUTCDate() + 1);

            if (monIndex(d.getUTCDay()) === 0) {
                const monthOfNext = d.getUTCMonth();
                if (monthOfNext !== lastMonth && d.getUTCFullYear() === year) {
                    monthLabels.push({
                        weekIndex,
                        label: d.toLocaleString(locale, { month: "short", timeZone: "UTC" }),
                    });
                    lastMonth = monthOfNext;
                }
                weekIndex++;
            }
        }

        const positives = cells.map(c => c.count).filter(c => c > 0).sort((a, b) => a - b);
        const q = (p: number) => (positives.length ? positives[Math.floor((positives.length - 1) * p)] : 1);
        const thresholds = [q(FIRST_QUARTILE), q(SECOND_QUARTILE), q(THIRD_QUARTILE)];

        for (const c of cells) c.level = levelForCount(c.count, thresholds) as DayCell["level"];

        return { cells, monthLabels, weeks: weekIndex };
    }, [attendanceDates, year, locale]);

    const weekdayLabelsFull = new Array(DAYS_IN_WEEK).fill(0).map((_, i) =>
        new Date(Date.UTC(REFERENCE_WEEKDAY_YEAR, 0, 1 + i))
            .toLocaleDateString(locale, { weekday: "short", timeZone: "UTC" })
            .replace(/\.$/, "")
    );
    const weekdayLabelIndices = compact ? COMPACT_WEEKDAY_LABEL_INDICES : FULL_WEEKDAY_LABEL_INDICES;
    const labelColWidth = getLabelColumnWidth(showWeekdayLabels, compact);
    const topPad = TOP_PADDING;
    const leftPad = labelColWidth;
    const width = leftPad + weeks * (cellSize + gap) - gap;
    const height = topPad + DAYS_IN_WEEK * (cellSize + gap) - gap;

    const levelColor = (lvl: number) => (lvl === 0 ? emptyColor : colors[lvl - 1]);

    const monthLabelFilter = (i: number) => (compact ? i % 2 === 0 : true);

    return (
        <svg
            role="img"
            aria-label={`Presence heatmap for ${year}`}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ display: "block", maxWidth: "100%" }}
        >
            {monthLabels.map((m, i) =>
                monthLabelFilter(i) ? (
                    <text
                        key={`m-${m.weekIndex}`}
                        x={leftPad + m.weekIndex * (cellSize + gap)}
                        y={12}
                        fontSize={compact ? COMPACT_FONT_SIZE : DEFAULT_FONT_SIZE}
                        fill="currentColor"
                        opacity="0.8"
                    >
                        {m.label}
                    </text>
                ) : null
            )}

            {showWeekdayLabels &&
                weekdayLabelIndices.map((idx) => (
                    <text
                        key={`w-${idx}`}
                        x={leftPad - WEEKDAY_LABEL_OFFSET}
                        y={topPad + idx * (cellSize + gap) + cellSize - 2}
                        fontSize={compact ? COMPACT_FONT_SIZE : DEFAULT_FONT_SIZE}
                        fill="currentColor"
                        opacity="0.7"
                        textAnchor="end"
                    >
                        {compact ? weekdayLabelsFull[idx].slice(0, 1) : weekdayLabelsFull[idx]}
                    </text>
                ))}

            {cells.map((c, i) => {
                const x = leftPad + c.weekIndex * (cellSize + gap);
                const y = topPad + c.weekday * (cellSize + gap);
                const title = `${c.date.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                })}`;
                return (
                    <rect
                        key={i}
                        x={x}
                        y={y}
                        width={cellSize}
                        height={cellSize}
                        rx={2}
                        ry={2}
                        fill={levelColor(c.level)}
                    >
                        <title>{title}</title>
                    </rect>
                );
            })}
        </svg>
    );
}
